import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { walletTypeColor } from "@lib/helpers/ColorHelper";
import { createYupSync } from "@lib/helpers/TableHelper";
import { walletSchema as yupSchema } from "@lib/schemas/wallet";
import {
  PaginationType,
  UseMutationProps,
  UseQueryProps,
} from "@lib/types/Api";
import { Wallet, WalletType } from "@lib/types/Billing";
import { SendByteWallet } from "@lib/types/SendByte";
import { Form, Input, Modal, Select, Space, Tag } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import useIntl from "react-intl/lib/src/components/useIntl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Wallet | SendByteWallet>;
  wallet?: Wallet | SendByteWallet;
  walletQuery: UseQueryProps<PaginationType<Wallet | SendByteWallet>>;
};

export default function BillingWalletModal({
  openModal,
  toggleModal,
  mutationQuery,
  wallet,
  walletQuery,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();

  const { mutateAsync, isPending } = mutationQuery;

  const initialValues = useMemo(() => {
    return (
      wallet ??
      ({
        name: "",
      } as Wallet)
    );
  }, [wallet]);

  useEffect(() => {
    if (typeof form?.setFieldsValue !== "undefined") {
      form?.setFieldsValue(initialValues);
    }
  }, [form, openModal, initialValues]);

  const handleClose = useCallback(() => {
    toggleModal(false);
    form?.resetFields();
  }, [toggleModal, form]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={wallet ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.wallet"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      {openModal && (
        <Form<Wallet>
          {...layout}
          form={form}
          name={!!wallet?.id ? `wallet-update-${wallet.id}` : "wallet-add"}
          onFinish={async (values) => {
            await mutateAsync({ ...values, id: wallet?.id });
            await walletQuery.refetch();
          }}
          style={{ maxWidth: 600, marginTop: 20 }}
          autoComplete="off"
          initialValues={wallet}
        >
          <Form.Item
            name="name"
            label={messages["common.walletName"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Input
              value={form?.getFieldValue("name")}
              onChange={({ target }) => {
                form?.setFieldValue(
                  "name",
                  (target as any).value.toUpperCase(),
                );
              }}
            />
          </Form.Item>
          <Form.Item
            name="type"
            label={messages["common.walletType"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="text"
              options={Object.values(WalletType).map((type) => ({
                label: (
                  <Tag color={walletTypeColor(type)}>
                    {messages["walletType"][type] as string}
                  </Tag>
                ),
                text: messages["walletType"][type] as string,
                value: type,
              }))}
            />
          </Form.Item>

          <StyledFlex style={{ justifyContent: "end" }}>
            <Space>
              {!wallet && (
                <ActionButton
                  type="reset"
                  onClick={handleClose}
                  loading={isPending}
                />
              )}

              <ActionButton
                type={!!wallet ? "save" : "add"}
                loading={isPending}
              />
            </Space>
          </StyledFlex>
        </Form>
      )}
    </Modal>
  );
}
